/*
 * cart-system.js — self-hosted cart/product interactive layer
 * Implements the standard Shopify (Dawn, MIT-licensed) custom elements that this
 * theme's Liquid markup expects but did not ship with:
 *   helpers (fetchConfig, trapFocus, pub/sub) + quantity-input, variant-selects,
 *   product-form, cart-drawer, cart-drawer-items, cart-items, cart-note, cart-remove-button.
 * Every definition is guarded so it never collides with anything already defined.
 * Based on Shopify Dawn (https://github.com/Shopify/dawn, MIT License).
 */
(function () {
  /* ---------------- helpers ---------------- */
  if (typeof window.getFocusableElements !== 'function') {
    window.getFocusableElements = function (container) {
      return Array.from(
        container.querySelectorAll(
          "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
        )
      );
    };
  }

  if (!window.__trapFocusHandlers) window.__trapFocusHandlers = {};
  if (typeof window.trapFocus !== 'function') {
    window.trapFocus = function (container, elementToFocus) {
      elementToFocus = elementToFocus || container;
      var elements = window.getFocusableElements(container);
      var first = elements[0];
      var last = elements[elements.length - 1];
      window.removeTrapFocus();
      window.__trapFocusHandlers.focusin = function (event) {
        if (event.target !== container && event.target !== last && event.target !== first) return;
        document.addEventListener('keydown', window.__trapFocusHandlers.keydown);
      };
      window.__trapFocusHandlers.focusout = function () {
        document.removeEventListener('keydown', window.__trapFocusHandlers.keydown);
      };
      window.__trapFocusHandlers.keydown = function (event) {
        if (event.code === undefined || event.code.toUpperCase() !== 'TAB') return;
        if (event.target === last && !event.shiftKey) { event.preventDefault(); first.focus(); }
        if ((event.target === container || event.target === first) && event.shiftKey) { event.preventDefault(); last.focus(); }
      };
      document.addEventListener('focusout', window.__trapFocusHandlers.focusout);
      document.addEventListener('focusin', window.__trapFocusHandlers.focusin);
      if (elementToFocus) elementToFocus.focus();
    };
  }
  if (typeof window.removeTrapFocus !== 'function') {
    window.removeTrapFocus = function (elementToFocus) {
      document.removeEventListener('focusin', window.__trapFocusHandlers.focusin);
      document.removeEventListener('focusout', window.__trapFocusHandlers.focusout);
      document.removeEventListener('keydown', window.__trapFocusHandlers.keydown);
      if (elementToFocus) elementToFocus.focus();
    };
  }
  if (typeof window.onKeyUpEscape !== 'function') {
    window.onKeyUpEscape = function (event) {
      if (event.code.toUpperCase() !== 'ESCAPE') return;
      var openDetailsElement = event.target.closest('details[open]');
      if (!openDetailsElement) return;
      var summaryElement = openDetailsElement.querySelector('summary');
      openDetailsElement.removeAttribute('open');
      summaryElement.setAttribute('aria-expanded', false);
      summaryElement.focus();
    };
  }
  if (typeof window.fetchConfig !== 'function') {
    window.fetchConfig = function (type) {
      type = type || 'json';
      return { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/' + type } };
    };
  }
  if (typeof window.debounce !== 'function') {
    window.debounce = function (fn, wait) {
      var t;
      return function () {
        var ctx = this, args = arguments;
        clearTimeout(t);
        t = setTimeout(function () { fn.apply(ctx, args); }, wait);
      };
    };
  }

  /* ---------------- pub/sub ---------------- */
  if (!window.PUB_SUB_EVENTS) {
    window.PUB_SUB_EVENTS = {
      cartUpdate: 'cart-update',
      quantityUpdate: 'quantity-update',
      variantChange: 'variant-change',
      cartError: 'cart-error',
    };
  }
  if (typeof window.subscribe !== 'function') {
    window.__subscribers = window.__subscribers || {};
    window.subscribe = function (eventName, callback) {
      if (window.__subscribers[eventName] === undefined) window.__subscribers[eventName] = [];
      window.__subscribers[eventName] = [].concat(window.__subscribers[eventName], callback);
      return function () {
        window.__subscribers[eventName] = window.__subscribers[eventName].filter(function (cb) { return cb !== callback; });
      };
    };
    window.publish = function (eventName, data) {
      if (window.__subscribers[eventName]) window.__subscribers[eventName].forEach(function (cb) { cb(data); });
    };
  }

  var routes = window.routes || {};

  /* ---------------- quantity-input ---------------- */
  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', class QuantityInput extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('.quantity__input') || this.querySelector('input[name="quantity"]');
        if (!this.input) return;
        this.changeEvent = new Event('change', { bubbles: true });
        this.input.addEventListener('change', this.onInputChange.bind(this));
        this.querySelectorAll('button').forEach((b) => b.addEventListener('click', this.onButtonClick.bind(this)));
      }
      onInputChange() { this.validateQtyRules(); }
      onButtonClick(event) {
        event.preventDefault();
        var previousValue = this.input.value;
        if (event.target.name === 'plus') this.input.stepUp(); else this.input.stepDown();
        if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
      }
      validateQtyRules() {
        var value = parseInt(this.input.value);
        if (this.input.min) {
          var buttonMinus = this.querySelector(".quantity__button[name='minus']");
          if (buttonMinus) buttonMinus.classList.toggle('disabled', value <= parseInt(this.input.min));
        }
        if (this.input.max) {
          var buttonPlus = this.querySelector(".quantity__button[name='plus']");
          if (buttonPlus) buttonPlus.classList.toggle('disabled', value >= parseInt(this.input.max));
        }
      }
    });
  }

  /* ---------------- cart-remove-button ---------------- */
  if (!customElements.get('cart-remove-button')) {
    customElements.define('cart-remove-button', class CartRemoveButton extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', (event) => {
          event.preventDefault();
          var cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
          if (cartItems) cartItems.updateQuantity(this.dataset.index, 0);
        });
      }
    });
  }

  /* ---------------- cart-items (and cart-drawer-items) ---------------- */
  if (!customElements.get('cart-items')) {
    class CartItems extends HTMLElement {
      constructor() {
        super();
        this.lineItemStatusElement =
          document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');
        var debouncedOnChange = window.debounce((event) => { this.onChange(event); }, 300);
        this.addEventListener('change', debouncedOnChange.bind(this));
      }
      connectedCallback() {
        this.cartUpdateUnsubscriber = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'cart-items') return;
          this.onCartUpdate();
        });
      }
      disconnectedCallback() { if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber(); }
      onChange(event) {
        this.updateQuantity(
          event.target.dataset.index,
          event.target.value,
          document.activeElement.getAttribute('name'),
          event.target.dataset.quantityVariantId
        );
      }
      onCartUpdate() {
        if (this.tagName === 'CART-DRAWER-ITEMS') {
          fetch(`${routes.cart_url}?section_id=cart-drawer`)
            .then((response) => response.text())
            .then((responseText) => {
              var html = new DOMParser().parseFromString(responseText, 'text/html');
              var selectors = ['cart-drawer-items', '.cart-drawer__footer'];
              for (var sel of selectors) {
                var target = document.querySelector(sel);
                var src = html.querySelector(sel);
                if (target && src) target.replaceWith(src);
              }
            })
            .catch((e) => console.error(e));
        } else {
          fetch(`${routes.cart_url}?section_id=main-cart-items`)
            .then((response) => response.text())
            .then((responseText) => {
              var html = new DOMParser().parseFromString(responseText, 'text/html');
              var sourceQty = html.querySelector('cart-items');
              if (sourceQty) this.innerHTML = sourceQty.innerHTML;
            })
            .catch((e) => console.error(e));
        }
      }
      getSectionsToRender() {
        return [
          { id: 'main-cart-items', section: document.getElementById('main-cart-items') ? document.getElementById('main-cart-items').dataset.id : 'main-cart-items', selector: '.js-contents' },
          { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
          { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
          { id: 'main-cart-footer', section: document.getElementById('main-cart-footer') ? document.getElementById('main-cart-footer').dataset.id : 'main-cart-footer', selector: '.js-contents' },
        ];
      }
      updateQuantity(line, quantity, name, variantId) {
        this.enableLoading(line);
        var body = JSON.stringify({
          line: line,
          quantity: quantity,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        });
        fetch(`${routes.cart_change_url}`, Object.assign({}, window.fetchConfig(), { body: body }))
          .then((response) => response.text())
          .then((state) => {
            var parsedState = JSON.parse(state);
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
            this.getSectionsToRender().forEach((section) => {
              var elementToReplace =
                (document.getElementById(section.id) && document.getElementById(section.id).querySelector(section.selector)) ||
                document.getElementById(section.id);
              if (!elementToReplace || !parsedState.sections[section.section]) return;
              elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
            });
            this.updateLiveRegions(line, parsedState.item_count);
            var drawerEl = document.querySelector('cart-drawer');
            if (drawerEl) drawerEl.classList.toggle('is-empty', (parsedState.item_count || 0) === 0);
            this.disableLoading(line);
          })
          .catch(() => { this.disableLoading(line); });
      }
      updateLiveRegions(line, itemCount) {
        if (this.lineItemStatusElement) {
          this.lineItemStatusElement.setAttribute('aria-hidden', true);
        }
      }
      getSectionInnerHTML(html, selector) {
        var parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
        return parsed ? parsed.innerHTML : '';
      }
      enableLoading(line) {
        var mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
        if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');
        var spinners = this.querySelectorAll(`#CartItem-${line} .loading-overlay, #CartDrawer-Item-${line} .loading-overlay`);
        spinners.forEach((o) => o.classList.remove('hidden'));
        document.activeElement.blur();
      }
      disableLoading(line) {
        var mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
        if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');
      }
    }
    customElements.define('cart-items', CartItems);
    window.CartItems = CartItems;

    if (!customElements.get('cart-drawer-items')) {
      customElements.define('cart-drawer-items', class CartDrawerItems extends CartItems {
        // Re-render the WHOLE #CartDrawer (header count + line items + footer subtotal/
        // savings/checkout) so quantity/remove changes inside the drawer update everything,
        // not just the line items. Mirrors how the theme's product-form.js refreshes on ATC.
        getSectionsToRender() {
          return [
            { id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' },
            { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
          ];
        }
      });
    }
  }

  /* ---------------- cart-note ---------------- */
  if (!customElements.get('cart-note')) {
    customElements.define('cart-note', class CartNote extends HTMLElement {
      constructor() {
        super();
        var input = this.querySelector('textarea');
        if (!input) return;
        this.addEventListener('change', window.debounce((event) => {
          var body = JSON.stringify({ note: event.target.value });
          fetch(`${routes.cart_update_url}`, Object.assign({}, window.fetchConfig(), { body: body }));
        }, 300));
      }
    });
  }

  /*
   * NOTE: product-form and cart-drawer are intentionally NOT defined here —
   * this theme ships its own custom assets/product-form.js and assets/cart-drawer.js
   * which own the Add-to-Cart submit + drawer open/close. This file only adds the
   * pieces that were missing (quantity-input, variant-selects, cart-items, etc.).
   */

  /* ---------------- variant-selects (standard Dawn variant switching) ---------------- */
  if (!customElements.get('variant-selects')) {
    customElements.define('variant-selects', class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('change', this.onVariantChange);
      }
      onVariantChange() {
        this.updateOptions();
        this.updateMasterId();
        this.updateSelectedVariant();
        if (!this.currentVariant) {
          this.toggleAddButton(true, '', true);
          this.setUnavailable();
        } else {
          this.updateURL();
          this.updateVariantInput();
          this.renderProductInfo();
          this.updateShareUrl && this.updateShareUrl();
        }
      }
      updateOptions() {
        // Build one entry per product option, IN DOCUMENT ORDER, because
        // updateMasterId() compares this list positionally against variant.options.
        //
        // Why this is not the stock Dawn one-liner: Dawn assumes every option group is a
        // <fieldset> (radios) or a <select>. This theme renders neither consistently —
        // a 1-option product gives loose radios inside <div class="product-form__input">,
        // and a 2-option product gives radios for the first option and a <select> for the
        // second. A fieldset/select-only scan therefore returned either an EMPTY list or a
        // SHORT list, no variant matched, currentVariant went undefined, and
        // setUnavailable() permanently disabled the Add to cart button. Silent: no console
        // error, no Liquid error. It stayed hidden because the source stores sell
        // single-variant kits.
        var self = this;
        var entries = [];
        var seenRadioName = {};
        this.querySelectorAll('select, fieldset, input[type="radio"]').forEach(function (el) {
          if (el.tagName === 'SELECT') { entries.push({ kind: 'select', el: el }); return; }
          if (el.tagName === 'FIELDSET') { entries.push({ kind: 'fieldset', el: el }); return; }
          // Loose radio: the fieldset entry already covers any radio inside one, and each
          // option group is one `name`, so take only the first radio of each name.
          if (el.closest('fieldset')) return;
          if (!el.name || seenRadioName[el.name]) return;
          seenRadioName[el.name] = true;
          entries.push({ kind: 'radio', name: el.name });
        });
        this.options = entries.map(function (entry) {
          if (entry.kind === 'select') return entry.el.value;
          var radios;
          if (entry.kind === 'fieldset') {
            radios = Array.from(entry.el.querySelectorAll('input'));
          } else {
            radios = Array.from(self.querySelectorAll('input[type="radio"]')).filter(
              function (r) { return r.name === entry.name && !r.closest('fieldset'); });
          }
          var checked = radios.find(function (r) { return r.checked; });
          return checked ? checked.value : (radios[0] ? radios[0].value : undefined);
        });
      }
      updateMasterId() {
        this.currentVariant = this.getVariantData().find((variant) => {
          return !variant.options.map((option, index) => this.options[index] === option).includes(false);
        });
      }
      updateSelectedVariant() {}
      updateURL() {
        if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
      }
      updateVariantInput() {
        var productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`);
        if (!productForms.length) productForms = document.querySelectorAll('product-form form');
        productForms.forEach((productForm) => {
          var input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = this.currentVariant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
      renderProductInfo() {
        var requestedVariantId = this.currentVariant.id;
        var sectionId = this.dataset.originalSection || this.dataset.section;
        fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            if (this.currentVariant.id !== requestedVariantId) return;
            var html = new DOMParser().parseFromString(responseText, 'text/html');
            // Inner-swap targets. `price-<section>` is the standalone price block, which
            // product-template-v2 ships DISABLED — so on that template every id here used
            // to miss and the shopper kept seeing the first variant's price while the cart
            // charged the selected one. `sticky-atc-separate-price` is the price the kit
            // PDP actually shows, so it has to be in this list.
            var idMap = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume',
                         'Quantity-Rules', 'sticky-atc-separate-price', 'sticky-atc-price'];
            idMap.forEach((key) => {
              var id = `${key}-${this.dataset.section}`;
              var dest = document.getElementById(id);
              var src = html.getElementById(id);
              if (dest && src) dest.innerHTML = src.innerHTML;
              if (dest && src && src.classList.contains('hidden')) dest.classList.add('hidden'); else if (dest) dest.classList.remove('hidden');
            });

            // Whole-node replacements. <quantity-breaks> keeps the tier base price in its
            // own data-base-price attribute and binds the tier `change` listeners in its
            // constructor, so an innerHTML swap would leave BOTH stale: the tier maths
            // would keep using the previous variant's price and the listeners would point
            // at detached inputs. Replacing the node re-runs the custom-element constructor.
            ['quantity-breaks'].forEach((key) => {
              var id = `${key}-${this.dataset.section}`;
              var dest = document.getElementById(id);
              var src = html.getElementById(id);
              if (!dest || !src) return;
              var checked = dest.querySelector('input[name="quantity"]:checked');
              var keep = checked ? checked.id : null;
              dest.replaceWith(src.cloneNode(true));
              // Preserve the shopper's tier selection across the swap.
              if (keep) {
                var again = document.getElementById(keep);
                if (again && !again.checked) {
                  again.checked = true;
                  again.dispatchEvent(new Event('change', { bubbles: true }));
                }
              }
            });
            // update ATC button availability
            var price = document.getElementById(`price-${this.dataset.section}`);
            if (price) price.classList.remove('hidden');
            this.toggleAddButton(!this.currentVariant.available, window.variantStrings ? window.variantStrings.soldOut : 'Sold out');
          })
          .catch((e) => console.error(e));
      }
      toggleAddButton(disable, text, modifyClass) {
        var productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        var addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;
        var addButtonText = addButton.querySelector('.main-atc__label__text, span');
        if (disable === undefined) disable = true;
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text && addButtonText) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          if (addButtonText && window.variantStrings) addButtonText.textContent = window.variantStrings.addToCart;
        }
      }
      setUnavailable() {
        this.toggleAddButton(true, window.variantStrings ? window.variantStrings.unavailable : 'Unavailable', true);
      }
      getVariantData() {
        this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }
    });
  }

  if (!customElements.get('variant-radios')) {
    var VS = customElements.get('variant-selects');
    if (VS) customElements.define('variant-radios', class VariantRadios extends VS {});
  }

  /* ---------------- resilient cart-drawer overlay close ----------------
   * The drawer contents are re-rendered (on ATC and on quantity/remove changes),
   * which re-creates #CartDrawer-Overlay and drops any directly-bound listener.
   * A delegated, document-level handler survives every re-render. */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (t && (t.id === 'CartDrawer-Overlay' || (t.classList && t.classList.contains('cart-drawer__overlay')))) {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.close === 'function') drawer.close();
    }
  });

  /* ---------------- header cart icon opens the drawer ----------------
   * When the cart type is "drawer", clicking the header cart icon (a link to /cart)
   * should open the drawer instead of navigating to the cart page. Delegated so it
   * survives header/cart re-renders. Links inside the drawer are left alone. */
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var link = e.target.closest('a');
    if (!link) return;
    var href = link.getAttribute('href') || '';
    if (!/\/cart(\?.*)?$/.test(href.split('#')[0])) return; // only links to the cart page
    if (link.closest('cart-drawer')) return;                 // not links inside the drawer
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      e.preventDefault();
      drawer.open(link);
    }
  });
})();
